import { apiFetch } from './client.js';

/**
 * Cliente de "quién usó OPUS ai y qué hizo" (solo staff Ecosoft).
 * El porqué, las dos lentes y el hueco conocido están en
 * `apps/api/src/routes/staffActividad.ts`.
 */

export interface UsuarioActivo {
  userId: string;
  email: string;
  nombre: string | null;
  /** Las empresas donde dejó rastro, separadas por coma. */
  empresa: string | null;
  plan: string | null;
  ultimaActividad: string;
  eventos: number;
  /** Intentó entrar y no pudo, ni una vez, en el período mirado. Va en rojo. */
  noPudoEntrar: boolean;
  /** El motivo, ya redactado por el servidor. `null` cuando sí entró. */
  porQueNoPudoEntrar: string | null;
  /** Cuántas veces lo rebotamos en la puerta. */
  intentosRechazados: number;
}

/** A quién mostrar. El servidor manda `con_plan` si no se le pide otra cosa. */
export type FiltroActividad = 'todos' | 'con_plan' | 'sin_plan';

export interface ActividadLista {
  dias: number;
  filtro: FiltroActividad;
  /** Siempre sobre la lista COMPLETA, filtre lo que filtre: dice cuántos se escondieron. */
  conteos: { conPlan: number; sinPlan: number; total: number };
  usuarios: UsuarioActivo[];
}

export interface ProyectoDeUsuario {
  id: string;
  nombre: string;
  creado: string;
  archivos: number;
  /** Estado crudo del presupuesto más reciente. `null` = nunca armó la hoja. */
  hoja: string | null;
  /** El mismo estado, ya en castellano. */
  hojaSeLee: string;
  conceptos: number | null;
  conPrecio: number | null;
  apus: number;
  ultimoMovimiento: string | null;
}

export interface PasoDelRato {
  cuando: string;
  action: string;
  queHizo: string;
  proyecto: string | null;
  metadata: Record<string, unknown> | null;
}

export interface RatoDeTrabajo {
  desde: string;
  hasta: string;
  duracionMin: number;
  proyectos: string[];
  pasos: PasoDelRato[];
}

/**
 * Las dos fechas que la pantalla tenía confundidas en una sola. Iniciar sesión y estar
 * activo no son lo mismo: mientras la persona trabaja su sesión se renueva sola, así que
 * el inicio de sesión puede quedar horas atrás. Las dos se calculan en el servidor y sin
 * recortar por el período elegido.
 */
export interface CuandoDeUsuario {
  /** Última vez que escribió su contraseña o volvió a entrar. `null` = sin registro. */
  inicioDeSesion: string | null;
  /** Lo último que hizo y quedó registrado. `null` = nunca hizo nada. */
  ultimaActividad: string | null;
  /** Qué fue eso último, ya en castellano. */
  ultimaActividadQueHizo: string | null;
}

export interface ActividadDeUsuario {
  usuario: { userId: string; email: string; nombre: string | null };
  dias: number;
  cuando: CuandoDeUsuario;
  proyectos: ProyectoDeUsuario[];
  ratos: RatoDeTrabajo[];
  /** Qué parte del relato falta. `null` = no falta nada. */
  huecoConocido: string | null;
}

export function getActividad(dias = 7, filtro: FiltroActividad = 'con_plan'): Promise<ActividadLista> {
  return apiFetch<ActividadLista>(`/api/v1/staff/actividad?dias=${dias}&filtro=${filtro}`);
}

export function getActividadDeUsuario(userId: string, dias = 30): Promise<ActividadDeUsuario> {
  return apiFetch<ActividadDeUsuario>(`/api/v1/staff/actividad/${userId}?dias=${dias}`);
}
